import { Box, Button } from '@mui/material'
import { ReactElement, useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { useGetCountriesQuery } from '../../../app/redux-fetch/apiCoreQuery'
import { getProperty, parseGeoCoderResult } from '../../../app/utils'
import ConditionalRender from '../../../components/ConditionalRender/ConditionalRender'
import FormInput from '../../../components/Forms/FormInput'
import FormSelect from '../../../components/Forms/FormSelect'
import PolygonMap from '../../../components/PolygonMap/PolygonMap'
import { SectionTitleTypography } from '../../../components/Typographies/styles'
import { LocationType } from '../../../types/common'
import { CustomerFormData } from '../../CustomerSettings/Customer/Components/validationSchema'
import { IMarker } from '../../Shipments/types'
import messages from '../messages'

type AddressDetailsProps = {
  isDisabled?: boolean
  locationTypeId?: number
}

export const getError = (propertyName: string, touched: any, errors: any): boolean => {
  const isTouched = getProperty(touched, propertyName)
  const errorMessage = getProperty(errors as unknown, propertyName)
  return isTouched && (errorMessage as any)
}

const AddressDetails = ({ isDisabled, locationTypeId }: AddressDetailsProps): ReactElement<any, any> => {
  const { formatMessage } = useIntl()
  const { setValue, watch } = useFormContext<CustomerFormData>()
  const [marker, setMarker] = useState<IMarker>()

  const values = watch()
  const [polygons, setPolygons] = useState(values.geofence ?? [])

  const { data: countriesResult = [] } = useGetCountriesQuery()

  const countries = countriesResult.map((country: any) => ({
    label: country.name,
    value: country.id,
    iso2: country.iso2,
    id: country.id,
  }))

  const updateMarker = (coords: any) => {
    if (coords.lat != marker?.lat || coords.lng != marker?.lng) {
      setMarker(!coords.lat || !coords.lng ? undefined : coords)
    }
  }

  useEffect(() => {
    if (updateMarker) {
      updateMarker({ lat: values.lat, lng: values.lng })
    }
  }, [values.lat, values.lng])

  useEffect(() => {
    if (JSON.stringify(values.geofence) !== JSON.stringify(polygons)) {
      setPolygons(values.geofence ?? [])
    }
  }, [values.geofence])

  const handleGeocoder = (geoCoderResult: any) => {
    const parsedAddress = parseGeoCoderResult(geoCoderResult.address_components)

    let address1 = geoCoderResult.formatted_address
    if (parsedAddress?.country) {
      const parsedCountry = countries.find((c: any) => c.iso2 === parsedAddress?.country.short_name)

      address1 = address1
        .replace(`, ${parsedAddress.country.long_name}`, '')
        .replace(`، ${parsedAddress.country.long_name}`, '')
        .replace(` ${parsedAddress.country.long_name}`, '')
      if (parsedCountry) {
        setValue('address.countryId', parsedCountry.id)
      }
    } else {
      setValue('address.countryId', 0)
    }
    if (parsedAddress?.postalCode) {
      setValue('address.postalCode', parsedAddress?.postalCode)
    } else {
      setValue('address.postalCode', '')
    }
    if (parsedAddress?.city) {
      setValue('address.city', parsedAddress?.city)
    } else {
      setValue('address.city', '')
    }
    if (parsedAddress?.state) {
      setValue('address.state', parsedAddress?.state)
    } else {
      setValue('address.state', '')
    }
    setValue('address.address1', address1)

    setValue('lat', geoCoderResult.geometry.location.lat())
    setValue('lng', geoCoderResult.geometry.location.lng())
  }

  const onHandleDeleteMarker = () => {
    setValue('lat', undefined)
    setValue('lng', undefined)
    setMarker(undefined)
  }

  const onHandleDeletePolygon = () => {
    setPolygons([])
    setValue('lat', undefined)
    setValue('lng', undefined)
    setValue('geofence', [])
    setMarker(undefined)
  }

  useEffect(() => {
    setValue('geofence', polygons)
  }, [polygons, setValue])

  return (
    <Box sx={{ width: '100%', pb: '16px' }}>
      <Box
        display={'flex'}
        sx={{
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: '16px',
        }}
      >
        <SectionTitleTypography>{formatMessage(messages.addressDetails)}</SectionTitleTypography>
        <Box>
          <Button
            variant='outlined'
            color='secondary'
            disabled={polygons && polygons.length === 0 || isDisabled}
            onClick={onHandleDeletePolygon}
            sx={{ marginRight: '12px' }}
          >
            {formatMessage(messages.deletePolygon)}
          </Button>
          <Button
            variant='outlined'
            color='secondary'
            disabled={!marker?.lat && !marker?.lng || isDisabled}
            onClick={onHandleDeleteMarker}
          >
            {formatMessage(messages.deleteMarker)}
          </Button>
        </Box>
      </Box>
      <Box>
        <PolygonMap
          googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ''}
          defaultCenter={{
            lat: values?.lat || 24.638517161652892,
            lng: values?.lng || 45.84234953431573,
            zoom: values?.lat ? 20 : 5,
          }}
          marker={marker}
          height={294}
          polygons={polygons}
          editable={!isDisabled}
          onChangePolygons={setPolygons}
          onChangeMarker={setMarker}
          handleGeocode={handleGeocoder}
        />
      </Box>

      <FormInput
        name="address.address1"
        label={formatMessage(messages.address1)}
        required
        disabled={isDisabled}
        maxLength={120}
      />

      <FormInput
        name="address.address2"
        label={formatMessage(messages.address2)}
        disabled={isDisabled}
        maxLength={120}
      />

      <FormInput
        name="address.address3"
        label={formatMessage(messages.address3)}
        disabled={isDisabled}
        maxLength={120}
      />

      <FormInput
        name="address.city"
        label={formatMessage(messages.city)}
        required
        disabled={isDisabled}
        maxLength={100}
      />

      <Box display='flex' columnGap={2}>
        <FormInput
          name="address.state"
          label={formatMessage(messages.state)}
          required
          disabled={isDisabled}
          maxLength={100}
        />
        <FormInput
          name="address.postalCode"
          label={formatMessage(messages.postCode)}
          required
          disabled={isDisabled}
          maxLength={16}
        />
      </Box>

      <FormSelect
        name="address.countryId"
        label={formatMessage(messages.country)}
        options={countries}
        required
        disabled={isDisabled}
      />

      <ConditionalRender condition={locationTypeId !== LocationType.Shipper} >
        <Box display='flex' columnGap={2}>
          <FormInput
            name="lat"
            label={formatMessage(messages.lat)}
            type="number"
            disabled={isDisabled}
            maxLength={12}
          />
          <FormInput
            name="lng"
            label={formatMessage(messages.lng)}
            type="number"
            disabled={isDisabled}
            maxLength={13}
          />
        </Box>
      </ConditionalRender>
    </Box>
  )
}

export default AddressDetails
